import {
  AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { ClassicEditor } from 'ckeditor5';
import { variableLabel } from './variables';

// ponytail: GPL key shows the "Powered by CKEditor" badge; swap in a commercial key here to remove it.
const LICENSE_KEY = 'GPL';
const VAR_CLASS = 'tdt-var';
const PALETTE = [
  ['#000000', 'Black'], ['#4D4D4D', 'Dark grey'], ['#999999', 'Grey'], ['#E6E6E6', 'Light grey'], ['#FFFFFF', 'White'],
  ['#05477B', 'Navy'], ['#1565C0', 'Blue'], ['#2E7D32', 'Green'], ['#EF6C00', 'Orange'], ['#C62828', 'Red'],
  ['#E3F2FD', 'Light blue'], ['#E8F5E9', 'Light green'], ['#FFF3E0', 'Light orange'], ['#FFEBEE', 'Light red'], ['#FFF9C4', 'Light yellow'],
].map(([color, label]) => ({ color, label, hasBorder: color === '#FFFFFF' }));

type Ck = typeof import('ckeditor5');

/** `@TOKEN@` variables as non-editable inline pills (can't be split by typing inside them). */
const variablePills = ({ Plugin, Widget, toWidget, viewToModelPositionOutsideModelElement }: Ck) => class VariablePills extends Plugin {
  static get requires() { return [Widget]; }

  init(): void {
    const editor = this.editor;
    editor.model.schema.register('tdtVariable', { inheritAllFrom: '$inlineObject', allowAttributes: ['token'] });
    editor.conversion.for('upcast').elementToElement({
      // Claim data-token too, or General HTML Support re-wraps the pill in a second <span data-token>.
      view: { name: 'span', classes: [VAR_CLASS], attributes: { 'data-token': true } },
      model: (el, { writer }) => writer.createElement('tdtVariable', { token: el.getAttribute('data-token') }),
      converterPriority: 'highest',
    });
    const pill = (token: string, writer: any) => {
      const span = writer.createContainerElement('span', { class: VAR_CLASS, 'data-token': token });
      writer.insert(writer.createPositionAt(span, 0), writer.createText(variableLabel(token)));
      return span;
    };
    editor.conversion.for('editingDowncast').elementToElement({
      model: 'tdtVariable',
      view: (m, { writer }) => toWidget(pill(m.getAttribute('token') as string, writer), writer),
    });
    editor.conversion.for('dataDowncast').elementToElement({
      model: 'tdtVariable',
      view: (m, { writer }) => pill(m.getAttribute('token') as string, writer),
    });
    editor.editing.mapper.on('viewToModelPosition',
      viewToModelPositionOutsideModelElement(editor.model, v => v.hasClass(VAR_CLASS)));
  }
};

/** Toolbar buttons that hand off to the page's attachment picker (images go out as cid: attachments). */
const fileButtons = ({ Plugin, ButtonView, IconImageUpload, IconBrowseFiles }: Ck, onImage: () => void, onFile: () => void) =>
  class FileButtons extends Plugin {
    init(): void {
      const add = (name: string, label: string, icon: string, run: () => void) =>
        this.editor.ui.componentFactory.add(name, locale => {
          const b = new ButtonView(locale);
          b.set({ label, icon, tooltip: true });
          b.on('execute', run);
          return b;
        });
      add('tdtInsertImage', 'Insert image', IconImageUpload, onImage);
      add('tdtAttachFile', 'Attach a file', IconBrowseFiles, onFile);
    }
  };

/**
 * Rich-text email editor (CKEditor 5) as a form control. The value stays plain HTML with raw
 * `@TOKEN@` variables and `cid:` images, exactly what the backend substitutes and sends.
 * `fullPage` keeps a template's <html><head><style> wrapper (notification templates).
 */
@Component({
  selector: 'app-html-editor',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HtmlEditorComponent), multi: true }],
  template: `<div class="html-editor" [class.invalid]="invalid"><div #host></div><div #counts class="html-editor-counts"></div></div>`,
  styles: [`
    .html-editor .ck-editor__editable_inline { min-height: 300px; max-height: 60vh; }
    .html-editor-counts { display: flex; justify-content: flex-end; padding: 4px 2px 0; }
    .html-editor-counts .ck-word-count { display: flex; gap: 12px; font-size: 12px; color: #64748B; }
    .html-editor.invalid .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused) { border-color: #C62828; }
    .html-editor .ck.ck-editor__main > .ck-editor__editable { background: #fff; }
    .html-editor .ck-content .${VAR_CLASS} { display: inline-block; background: #E8EEF8; color: #0B4A8B; border-radius: 999px;
      padding: 0 9px; font-size: .92em; font-weight: 500; line-height: 1.6; }
    .html-editor .ck-content .${VAR_CLASS}::before { content: '{'; opacity: .45; }
    .html-editor .ck-content .${VAR_CLASS}::after { content: '}'; opacity: .45; }
    .html-editor .ck-content .${VAR_CLASS}.ck-widget_selected { outline: 2px solid #1976D2; }
  `],
})
export class HtmlEditorComponent implements ControlValueAccessor, AfterViewInit, DoCheck, OnDestroy {
  @Input() fullPage = false;
  @Input() invalid = false;
  /** Supplies cid → displayable URL (the attachments component), so `cid:` images show while editing. */
  @Input() cidSource?: { cidUrls: Record<string, string> } | null;
  /** Bound by pages that support attachments; adds Insert image / Attach file to the toolbar. */
  @Output() addImage = new EventEmitter<void>();
  @Output() attachFile = new EventEmitter<void>();
  @ViewChild('host') host!: ElementRef<HTMLElement>;
  @ViewChild('counts') counts!: ElementRef<HTMLElement>;

  private editor?: ClassicEditor;
  private value = '';
  private disabled = false;
  private cidKey = '';
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  async ngAfterViewInit(): Promise<void> {
    // Loaded on demand: CKEditor is ~1.5 MB and only these pages need it.
    const ck = await import('ckeditor5');
    const {
      Alignment, Autoformat, Bold, ClassicEditor, Essentials, FontColor, FullPage, GeneralHtmlSupport, Heading,
      HorizontalLine, ImageBlock, ImageInline, ImageResize, ImageTextAlternative, ImageToolbar, Italic, Link, LinkImage,
      List, Paragraph, PasteFromOffice, RemoveFormat, SourceEditing, Table, TableToolbar, Underline,
      FontSize, FontFamily, FontBackgroundColor, Strikethrough, Subscript, Superscript, Indent, IndentBlock, BlockQuote,
      ListProperties, SpecialCharacters, SpecialCharactersEssentials, TableProperties, TableCellProperties,
      FindAndReplace, Fullscreen, WordCount, ShowBlocks,
    } = ck;
    const files = this.addImage.observed;
    const editor = await ClassicEditor.create(this.host.nativeElement, {
      licenseKey: LICENSE_KEY,
      plugins: [Essentials, Paragraph, Heading, Bold, Italic, Underline, Link, List, Alignment, FontColor,
        Table, TableToolbar, ImageBlock, ImageInline, ImageResize, ImageToolbar, ImageTextAlternative, LinkImage, HorizontalLine, RemoveFormat, Autoformat, PasteFromOffice,
        GeneralHtmlSupport, SourceEditing, variablePills(ck),
        FontSize, FontFamily, FontBackgroundColor, Strikethrough, Subscript, Superscript, Indent, IndentBlock, BlockQuote,
        ListProperties, SpecialCharacters, SpecialCharactersEssentials, TableProperties, TableCellProperties,
        FindAndReplace, Fullscreen, WordCount, ShowBlocks,
        ...(files ? [fileButtons(ck, () => this.addImage.emit(), () => this.attachFile.emit())] : []), ...(this.fullPage ? [FullPage] : [])],
      toolbar: {
        items: ['undo', 'redo', '|', 'heading', 'fontFamily', 'fontSize', '|',
          'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', '|',
          'fontColor', 'fontBackgroundColor', 'removeFormat', '|',
          'alignment', 'bulletedList', 'numberedList', 'outdent', 'indent', 'blockQuote', '|',
          'link', ...(files ? ['tdtInsertImage', 'tdtAttachFile'] : []), 'insertTable', 'horizontalLine', 'specialCharacters', '|',
          'findAndReplace', 'showBlocks', 'fullscreen', 'sourceEditing'],
        // Wrap onto a second row instead of hiding tools behind a "more" menu.
        shouldNotGroupWhenFull: true,
      },
      heading: { options: [
        { model: 'paragraph', title: 'Normal text', class: 'ck-heading_paragraph' },
        { model: 'heading2', view: 'h2', title: 'Heading', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Subheading', class: 'ck-heading_heading3' },
      ] },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', '|', 'tableProperties', 'tableCellProperties'],
        tableProperties: { borderColors: PALETTE, backgroundColors: PALETTE, colorPicker: { format: 'hex' } },
        tableCellProperties: { borderColors: PALETTE, backgroundColors: PALETTE, colorPicker: { format: 'hex' } },
      },
      // Pixel sizes and named web-safe fonts: saved as inline styles, which email clients keep (named sizes become classes).
      fontSize: { options: [12, 14, 'default', 18, 22, 28, 36] },
      fontFamily: { options: ['default', 'Arial, Helvetica, sans-serif', 'Georgia, serif', 'Tahoma, Geneva, sans-serif',
        'Times New Roman, Times, serif', 'Trebuchet MS, Helvetica, sans-serif', 'Verdana, Geneva, sans-serif',
        'Courier New, Courier, monospace'] },
      list: { properties: { styles: true, startIndex: true, reversed: false } },
      wordCount: { displayWords: true, displayCharacters: true },
      // Hex colours only: Outlook ignores hsl(), which is CKEditor's default palette format.
      fontColor: { colors: PALETTE, colorPicker: { format: 'hex' } },
      fontBackgroundColor: { colors: PALETTE, colorPicker: { format: 'hex' } },
      // Click an image: drag handles or the size menu. Pixels (not %) so email clients honour it;
      // centre an image with the paragraph alignment button (image style classes don't survive email).
      image: {
        resizeUnit: 'px',
        resizeOptions: [
          { name: 'resizeImage:original', label: 'Original size', value: null },
          { name: 'resizeImage:150', label: 'Small (150px)', value: '150' },
          { name: 'resizeImage:300', label: 'Medium (300px)', value: '300' },
          { name: 'resizeImage:450', label: 'Large (450px)', value: '450' },
          { name: 'resizeImage:custom', label: 'Custom…', value: 'custom' },
        ],
        toolbar: ['resizeImage', '|', 'imageTextAlternative', 'linkImage'],
      },
      link: { defaultProtocol: 'https://' },
      htmlSupport: {
        allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
        disallow: [{ name: /^(script|iframe|object|embed)$/ }, { name: /.*/, attributes: /^on/ }],
        ...(this.fullPage ? { fullPage: { allowRenderStylesFromHead: true } } : {}),
      },
    });
    this.editor = editor;
    this.counts.nativeElement.appendChild((editor.plugins.get('WordCount') as any).wordCountContainer);
    editor.setData(this.toEditor(this.value));
    if (this.disabled) editor.enableReadOnlyMode('form');
    editor.model.document.on('change:data', () => {
      this.value = this.toValue(editor.getData());
      this.onChange(this.value);
    });
    editor.editing.view.document.on('blur', () => this.onTouched());
  }

  ngDoCheck(): void {
    // Attachments can load after the body: re-render once the cid → URL map changes.
    const key = JSON.stringify(this.cidSource?.cidUrls ?? {});
    if (key === this.cidKey) return;
    this.cidKey = key;
    this.editor?.setData(this.toEditor(this.value));
  }

  ngOnDestroy(): void { this.editor?.destroy(); }

  writeValue(v: string | null): void {
    if ((v ?? '') === this.value) return;
    this.value = v ?? '';
    this.editor?.setData(this.toEditor(this.value));
  }
  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void {
    this.disabled = d;
    if (!this.editor) return;
    d ? this.editor.enableReadOnlyMode('form') : this.editor.disableReadOnlyMode('form');
  }

  /** Insert a `@TOKEN@` (as a pill) or an HTML snippet such as `<img src="cid:…">` at the caret. */
  insert(html: string): void {
    const editor = this.editor;
    if (!editor) return;
    editor.model.change(writer => {
      if (/^@[A-Z_]+@$/.test(html)) {
        editor.model.insertObject(writer.createElement('tdtVariable', { token: html }), null, null, { setSelection: 'after' });
      } else {
        editor.model.insertContent(editor.data.toModel(editor.data.processor.toView(this.toEditor(html))));
      }
    });
    editor.editing.view.focus();
  }

  /** Stored HTML → editor HTML: token text becomes pills, cid images point at viewable URLs. */
  private toEditor(html: string): string {
    if (!html) return '';
    const doctype = /^\s*<!doctype[^>]*>/i.exec(html)?.[0] ?? '';
    const isDoc = /<body[\s>]/i.test(html);
    const doc = new DOMParser().parseFromString(isDoc ? html : `<body>${html}</body>`, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) texts.push(walker.currentNode as Text);
    for (const t of texts) {
      const parts = t.data.split(/(@[A-Z_]+@)/);
      if (parts.length === 1) continue;
      t.replaceWith(...parts.map(p => {
        if (!/^@[A-Z_]+@$/.test(p)) return p;
        const s = doc.createElement('span');
        s.className = VAR_CLASS;
        s.setAttribute('data-token', p);
        s.textContent = variableLabel(p);
        return s;
      }));
    }
    const urls = this.cidSource?.cidUrls ?? {};
    doc.querySelectorAll('img[src^="cid:"]').forEach(img => {
      const url = urls[img.getAttribute('src')!.slice(4)];
      if (url) img.setAttribute('src', url);
    });
    return isDoc ? doctype + doc.documentElement.outerHTML : doc.body.innerHTML;
  }

  /** Editor HTML → stored HTML: pills back to raw tokens, viewable URLs back to `cid:`. */
  private toValue(html: string): string {
    // Twice: unwraps any leftover <span data-token> wrapper around a pill.
    const pill = /<span[^>]*data-token="(@[A-Z_]+@)"[^>]*>[^<]*<\/span>/g;
    let out = html.replace(pill, '$1').replace(pill, '$1');
    // Outlook ignores CSS widths on images: the width attribute must match the resized width
    // (CKEditor keeps the original width/height attributes, which Outlook would use instead).
    out = out.replace(/<img\b[^>]*>/g, tag => {
      const w = /style="(?:[^"]*;)?\s*width:\s*(\d+(?:\.\d+)?)px/.exec(tag)?.[1]; // not max-width
      if (!w) return tag;
      return tag.replace(/\s(?:width|height)="[^"]*"/g, '').replace(/^<img/, `<img width="${Math.round(+w)}"`);
    });
    for (const [cid, url] of Object.entries(this.cidSource?.cidUrls ?? {})) {
      out = out.split(`src="${url}"`).join(`src="cid:${cid}"`)
        .split(`src="${url.replace(/&/g, '&amp;')}"`).join(`src="cid:${cid}"`);
    }
    return out;
  }
}

